import React, {useEffect, useMemo, useRef} from "react";
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import ContainedTextField from "../../contained_text_field";
import validateDecisionQuestions from "./validate_decision_questions";
import createListKeys from "./list_keys";
import {
  DecisionQuestionOption,
  DecisionQuestionRow,
  DecisionQuestionsArgument,
} from "common/flowmaster/types/task_config";

const TYPES: Array<{key: DecisionQuestionRow["type"]; name: string}> = [
  {key: "predicate", name: "Yes / no"},
  {key: "choice", name: "Choice"},
  {key: "score", name: "Score"},
];

const styles = {
  row: {
    border: "1px solid #ddd",
    borderRadius: "4px",
    padding: "0.5em 0.75em",
    marginBottom: "0.5em",
  },
  rowHeader: {display: "flex", alignItems: "center", gap: "0.75em"},
  options: {marginLeft: "2em", marginTop: "0.5em"},
  option: {display: "flex", alignItems: "center", gap: "0.5em"},
  errors: {
    backgroundColor: "#ffebee",
    border: "1px solid #f44336",
    borderRadius: "4px",
    padding: "8px 12px",
    marginBottom: "10px",
    color: "#c62828",
    whiteSpace: "pre-wrap",
  },
} as const;

interface DecisionQuestionsEditorProps {
  argValue: DecisionQuestionsArgument;
  onUpdateItem: (value: DecisionQuestionsArgument) => void;
  handleHasError?: (hasError: boolean) => void;
  disabled?: boolean;
}

const DecisionQuestionsEditor: React.FC<DecisionQuestionsEditorProps> = ({
  argValue,
  onUpdateItem,
  handleHasError,
  disabled = false,
}) => {
  const rows = useMemo(
    () => (Array.isArray(argValue?.value) ? argValue.value : []),
    [argValue],
  );
  const errors = useMemo(() => validateDecisionQuestions(rows), [rows]);
  const listKeys = useRef(createListKeys()).current;
  const rowKeys = listKeys.keysFor("rows", rows.length);
  const switchedOn = rows.filter(row => row.enabled !== false).length;

  useEffect(() => {
    handleHasError?.(errors.length > 0);
  }, [errors, handleHasError]);

  const update = (newRows: DecisionQuestionRow[]) =>
    onUpdateItem({value: newRows});
  const updateRow = (index: number, changes: Partial<DecisionQuestionRow>) =>
    update(rows.map((row, i) => (i === index ? {...row, ...changes} : row)));
  const updateOptions = (index: number, options: DecisionQuestionOption[]) =>
    updateRow(index, {options});

  return (
    <div style={{width: "100%"}}>
      <Typography variant="body2" style={{marginBottom: "0.5em"}}>
        {`${rows.length} checks, ${switchedOn} switched on`}
      </Typography>
      {errors.length > 0 && (
        <div style={styles.errors}>{errors.join("\n")}</div>
      )}
      {rows.map((row, index) => {
        const options = row.options ?? [];
        const rowKey = rowKeys[index];
        const optionKeys = listKeys.keysFor(rowKey, options.length);
        return (
          <Box key={rowKey} style={styles.row}>
            <div style={styles.rowHeader}>
              <FormControlLabel
                label="On"
                control={
                  <Switch
                    size="small"
                    checked={row.enabled !== false}
                    disabled={disabled}
                    onChange={event =>
                      updateRow(index, {enabled: event.target.checked})
                    }
                  />
                }
              />
              <div style={{width: "14em"}}>
                <ContainedTextField
                  label="Name"
                  initialValue={row.name ?? ""}
                  onUpdate={name => updateRow(index, {name: name.trim()})}
                />
              </div>
              <Select
                value={row.type ?? "predicate"}
                disabled={disabled}
                onChange={event =>
                  updateRow(index, {
                    type: event.target.value as DecisionQuestionRow["type"],
                  })
                }
              >
                {TYPES.map(type => (
                  <MenuItem key={type.key} value={type.key}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
              <IconButton
                disabled={disabled}
                onClick={() => {
                  listKeys.remove("rows", index);
                  update(rows.filter((_, i) => i !== index));
                }}
              >
                <DeleteIcon />
              </IconButton>
            </div>
            <ContainedTextField
              label="Question"
              multiline
              fullWidth
              initialValue={row.question ?? ""}
              onUpdate={question => updateRow(index, {question})}
            />
            {row.type !== "predicate" && (
              <div style={styles.options}>
                {options.map((option, optionIndex) => (
                  <div key={optionKeys[optionIndex]} style={styles.option}>
                    <div style={{width: "12em"}}>
                      <ContainedTextField
                        label={row.type === "score" ? "Level" : "Option"}
                        initialValue={option.label ?? ""}
                        onUpdate={label =>
                          updateOptions(
                            index,
                            options.map((o, i) =>
                              i === optionIndex ? {...o, label} : o,
                            ),
                          )
                        }
                      />
                    </div>
                    <ContainedTextField
                      label="Description"
                      fullWidth
                      initialValue={option.description ?? ""}
                      onUpdate={description =>
                        updateOptions(
                          index,
                          options.map((o, i) =>
                            i === optionIndex ? {...o, description} : o,
                          ),
                        )
                      }
                    />
                    <IconButton
                      disabled={disabled}
                      onClick={() => {
                        listKeys.remove(rowKey, optionIndex);
                        updateOptions(
                          index,
                          options.filter((_, i) => i !== optionIndex),
                        );
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </div>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  disabled={disabled}
                  onClick={() =>
                    updateOptions(index, [...options, {label: ""}])
                  }
                >
                  {row.type === "score" ? "Add level" : "Add option"}
                </Button>
              </div>
            )}
          </Box>
        );
      })}
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        disabled={disabled}
        onClick={() =>
          update([
            ...rows,
            {enabled: true, name: "", type: "predicate", question: ""},
          ])
        }
      >
        Add check
      </Button>
    </div>
  );
};

export default DecisionQuestionsEditor;
